class ShippingCalculator extends HTMLElement {
  constructor() {
    super();
    
    // 1. Configurações injetadas via data-attributes no Liquid
    this.config = {
      shopDomain: this.dataset.shop,
      fallbackVariantGid: this.dataset.variantGid,
      freeShippingThreshold: parseFloat(this.dataset.freeShippingThreshold) || null,
      currencyLocale: this.dataset.currencyLocale || 'pt-BR'
    };

    // 2. Mapeamento de elementos do DOM interno
    this.elements = {
      cep: this.querySelector('#bp-cep'),
      btn: this.querySelector('#bp-calc'),
      results: this.querySelector('#bp-ship-results'),
      error: this.querySelector('#bp-ship-error'),
      loading: this.querySelector('#bp-ship-loading'),
      addr: this.querySelector('#bp-cep-addr'),
      addrText: this.querySelector('#bp-cep-addr-text'),
      saveToggle: this.querySelector('#bp-cep-save'),
      freebar: this.querySelector('#bp-freebar'),
      freeText: this.querySelector('#bp-freebar-text'),
      freeFill: this.querySelector('#bp-freebar-fill')
    };

    // 3. Chaves de Storage
    this.storageKeys = {
      lastCep: 'bp_ship_last_cep',
      cepCache: 'bp_ship_cep_cache_v2',
      consent: 'bp_ship_save_consent'
    };

    this.debounceTimer = null;
    this.calculate = this.calculate.bind(this);
  }

  connectedCallback() {
    this.initEvents();
    this.initConsentAndAutoFill();
  }

  initEvents() {
    this.elements.btn?.addEventListener('click', this.calculate);
    
    this.elements.cep?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.calculate();
      }
    });

    this.elements.cep?.addEventListener('input', (e) => {
      e.target.value = this.maskCep(e.target.value);
      this.elements.addr.hidden = true; 
    });

    this.elements.saveToggle?.addEventListener('change', (e) => {
      this.setConsent(e.target.checked);
      const rawCep = this.normalizeCep(this.elements.cep.value);
      if (e.target.checked && rawCep.length === 8) {
        localStorage.setItem(this.storageKeys.lastCep, rawCep);
      }
    });
  }
  /* ================= LÓGICA DE CÁLCULO ================= */

  debouncedCalculate() {
    clearTimeout(this.debounceTimer);
    if (this.normalizeCep(this.elements.cep?.value).length === 8) {
      this.debounceTimer = setTimeout(() => this.calculate(), 400);
    }
  }

  async calculate() {
    const rawCep = this.normalizeCep(this.elements.cep.value);
    if (rawCep.length !== 8) {
      this.showError('Digite um CEP válido (8 dígitos).');
      return;
    }

    this.setState('skeleton');

    try {
      const address = await this.lookupViaCep(rawCep);
      if (address) {
        if (this.elements.saveToggle.checked) localStorage.setItem(this.storageKeys.lastCep, rawCep);
        this.showAddressLabel(address);
      }

      const variantGid = this.getDynamicVariantGid();
      const qty = this.getPageQuantity();

      this.updateFreeShippingBar(qty);

      // Cotação via AJAX (Simula o carrinho para acionar apps de terceiros)
      const options = await this.quoteUsingAjax(variantGid, qty, address || { zip: rawCep, country: 'Brazil' });
      
      this.renderOptions(options);

    } catch (error) {
      console.error('Erro no cálculo de frete:', error);
      this.showError('Não foi possível calcular o frete agora. Tente novamente.');
    } finally {
      this.elements.btn?.classList.remove('is-loading');
    }
  }

  getDynamicVariantGid() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) return `gid://shopify/ProductVariant/${urlVariant}`;

    const formInput = document.querySelector('form[action*="/cart/add"] [name="id"], select[name="id"]');
    if (formInput && formInput.value) {
      const val = formInput.value;
      return val.includes('gid://') ? val : `gid://shopify/ProductVariant/${val}`;
    }
    return this.config.fallbackVariantGid;
  }

  getPageQuantity() {
    const qtyInput = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
    const qty = parseInt(qtyInput?.value, 10);
    return (Number.isFinite(qty) && qty > 0) ? qty : 1;
  }

  /* ================= COTAÇÃO VIA AJAX ================= */
  async quoteUsingAjax(variantGid, qty, addr) {
    const numericVariantId = parseInt(String(variantGid).split('/').pop(), 10);
    
    // 1. Snapshot do carrinho atual
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    const originalItems = {};
    (cart.items || []).forEach(it => { originalItems[it.variant_id] = it.quantity; });

    const prevQty = originalItems[numericVariantId] || 0;

    try {
      // 2. Adiciona o item silenciosamente
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: numericVariantId, quantity: qty }] })
      });
      
      if (!addRes.ok) return [];

      // 3. Consulta fretes (Engatilha os Apps Brasileiros)
      const qs = new URLSearchParams();
      qs.set('shipping_address[zip]', this.maskCep(addr.zip)); // Envia com traço
      qs.set('shipping_address[country]', 'Brazil');
      if (addr.province) qs.set('shipping_address[province]', addr.province);
      if (addr.city) qs.set('shipping_address[city]', addr.city);

      const ratesRes = await fetch(`/cart/shipping_rates.json?${qs.toString()}`);
      if (!ratesRes.ok) throw new Error('Falha ao buscar fretes de apps.');
      
      const ratesData = await ratesRes.json();
      
      return (ratesData.shipping_rates || []).map(rt => ({
        handle: rt.code || rt.name,
        title: rt.name,
        description: rt.presentation || rt.delivery_time || 'Entrega estimada',
        estimatedCost: { amount: rt.price, currencyCode: 'BRL' }
      }));

    } finally {
      // 4. Reverte o carrinho para o estado original em background
      const updates = { ...originalItems };
      updates[numericVariantId] = prevQty; 
      
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates })
      });
    }
  }

  /* ================= INTEGRAÇÕES SECUNDÁRIAS ================= */

  async lookupViaCep(cep) {
    const cache = this.getCepCache();
    if (cache[cep] && cache[cep].street) return cache[cep];

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data.erro) return null;

      const result = {
        zip: cep,
        country: 'Brazil',
        province: data.uf,
        city: data.localidade,
        street: data.logradouro,
        neighborhood: data.bairro
      };
      
      cache[cep] = result;
      localStorage.setItem(this.storageKeys.cepCache, JSON.stringify(cache));
      return result;
    } catch {
      return null;
    }
  }

  async updateFreeShippingBar(qty) {
    if (!this.config.freeShippingThreshold || !this.elements.freebar) return;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      let cartTotal = (cart.total_price || 0) / 100;

      let itemPrice = 0;
      const priceEl = document.querySelector('.price-item--regular, [data-product-price]');
      if (priceEl) {
        const priceText = priceEl.textContent.replace(/[^\d,.-]/g, '').replace(',', '.');
        itemPrice = parseFloat(priceText) || 0;
      }

      const totalSimulated = cartTotal + (itemPrice * qty);
      const threshold = this.config.freeShippingThreshold;
      const left = Math.max(0, threshold - totalSimulated);
      const pct = Math.max(0, Math.min(1, totalSimulated / threshold));

      this.elements.freebar.hidden = false;
      this.elements.freeFill.style.width = `${(pct * 100).toFixed(0)}%`;
      this.elements.freeText.textContent = left > 0 
        ? `Faltam ${this.money(left, 'BRL')} para frete grátis` 
        : `Você ganhou frete grátis!`;
    } catch (e) {
      this.elements.freebar.hidden = true;
    }
  }

  /* ================= UI E UTILITÁRIOS ================= */

  setState(state) {
    this.elements.error.hidden = true;
    this.elements.loading.hidden = true;
    this.elements.results.hidden = true;
    
    if (state === 'skeleton') {
      this.elements.btn?.classList.add('is-loading');
      this.renderSkeletons();
      this.elements.results.hidden = false;
    }
    if (state === 'results') {
      this.elements.btn?.classList.remove('is-loading');
      this.elements.results.hidden = false;
    }
  }

  showError(msg) {
    this.setState('');
    this.elements.error.textContent = msg;
    this.elements.error.hidden = false;
  }

  renderSkeletons() {
    this.elements.results.innerHTML = Array(3).fill(`
      <div class="bp-ship__skeleton">
        <div class="bp-ship__sk-left">
          <div class="bp-ship__sk-line lg"></div>
          <div class="bp-ship__sk-line sm"></div>
        </div>
        <div class="bp-ship__sk-price"></div>
      </div>
    `).join('');
  }

  renderOptions(options) {
    if (!options || options.length === 0) {
      this.elements.results.innerHTML = `
        <div class="bp-ship__card">
          <div class="bp-ship__title">Não encontramos opções para este CEP</div>
          <div class="bp-ship__price">—</div>
        </div>`;
      this.setState('results');
      return;
    }

    options.sort((a, b) => (Number(a.estimatedCost?.amount) || 0) - (Number(b.estimatedCost?.amount) || 0));

    this.elements.results.innerHTML = options.map((opt, index) => {
      const price = Number(opt.estimatedCost?.amount);
      const isCheapest = index === 0 && price > 0;
      const isFree = price === 0;

      let badge = '';
      if (isFree) {
        badge = `<span class="bp-ship__badge" style="background:#111;color:#fff;border-color:#111">Frete Grátis</span>`;
      } else if (isCheapest) {
        badge = `<span class="bp-ship__badge"><svg viewBox="0 0 24 24" style="width:12px"><path fill="#111" d="M21 8.5v5.3c0 .53-.21 1.04-.59 1.41l-5.2 5.2a2 2 0 0 1-2.83 0L4.59 11.92A2 2 0 0 1 4 10.5V6.5A2.5 2.5 0 0 1 6.5 4h4c.53 0 1.04.21 1.41.59l8.09 8.09Z"/></svg> Mais barato</span>`;
      }

      return `
        <div class="bp-ship__card">
          <div>
            <div class="bp-ship__title">${opt.title || 'Frete'} ${badge}</div>
            <div class="bp-ship__eta">${opt.description || 'Entrega estimada'}</div>
          </div>
          <div class="bp-ship__price">${isFree ? 'Grátis' : this.money(price, opt.estimatedCost?.currencyCode)}</div>
        </div>
      `;
    }).join('');
    
    this.setState('results');
  }

  showAddressLabel(data) {
    if (!this.elements.addrText) return;
    const left = [data.street, data.neighborhood].filter(Boolean).join(', ');
    const right = [data.city, data.province].filter(Boolean).join(' - ');
    const label = [left, right].filter(Boolean).join(' – ');
    
    if (label) {
      this.elements.addrText.textContent = label;
      this.elements.addr.hidden = false;
    }
  }

  initConsentAndAutoFill() {
    let storedConsent = localStorage.getItem(this.storageKeys.consent);
    if (storedConsent === null) {
      this.setConsent(true);
      if(this.elements.saveToggle) this.elements.saveToggle.checked = true;
    } else {
      if(this.elements.saveToggle) this.elements.saveToggle.checked = (storedConsent === '1');
    }

    if (this.elements.saveToggle?.checked) {
      const lastCep = localStorage.getItem(this.storageKeys.lastCep);
      if (lastCep && this.elements.cep) {
        this.elements.cep.value = this.maskCep(lastCep);
        this.lookupViaCep(lastCep).then(addr => {
          if (addr) this.showAddressLabel(addr);
        });
      }
    }
  }

  /* --- Helpers --- */
  normalizeCep = (cep) => (cep || '').replace(/\D/g, '').slice(0, 8);
  maskCep = (cep) => {
    let v = this.normalizeCep(cep);
    return v.length > 5 ? v.slice(0, 5) + '-' + v.slice(5) : v;
  };
  money = (amount, currency = 'BRL') => {
    return new Intl.NumberFormat(this.config.currencyLocale, { style: 'currency', currency }).format(amount);
  };
  setConsent = (isOn) => {
    if (isOn) {
      localStorage.setItem(this.storageKeys.consent, '1');
    } else {
      localStorage.removeItem(this.storageKeys.consent);
      localStorage.removeItem(this.storageKeys.lastCep);
    }
  };
  getCepCache = () => {
    try { return JSON.parse(localStorage.getItem(this.storageKeys.cepCache) || '{}'); } 
    catch { return {}; }
  };
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}